/**
 * How a campaign run is named, and whether it is collecting.
 *
 * A yearly drive names a year beside the drive; a run-once drive has no year
 * and is read as its own name. Whether a run is collecting follows from its
 * window: a missing end means it is still taking money, not that it never was.
 */

import { normalizeShortId } from './short-id'

/** The name a donor or member reads: the drive's own name, with a year only
 *  when the run has one. */
export function campaignDisplayLabel(
  label: string,
  period: string | null | undefined,
): string {
  return period ? `${label} ${period}` : label
}

/**
 * Whether this run is collecting at instant `at`.
 *
 * A null end is still collecting — open-ended until the office closes it.
 * Every reader that would have written `now < endsAt` goes through here, so a
 * missing end cannot be treated as "already over" at one site and "still open"
 * at another.
 *
 * The end is exclusive: a run whose end has arrived has ended. A close writes
 * the moment it happens as the end, and every "already closed" check reads
 * `endsAt <= now`, so a run cannot be both closed and still collecting in the
 * instant it was closed.
 */
export function isRunCollecting(
  run: { startsAt: Date; endsAt: Date | null },
  at: Date,
): boolean {
  return run.startsAt <= at && (run.endsAt === null || at < run.endsAt)
}

/**
 * The most other campaigns one campaign's page offers beside its own gift.
 *
 * Each is a field the donor reads before paying, and every one travels in a
 * single Stripe metadata value of 500 characters: five lines of two ids and a
 * figure fit in it, and a sixth would not.
 */
export const MAX_COMPANIONS = 5

/**
 * A readable name an office gives a campaign, used in its address in place of
 * the id: lowercase words joined by hyphens.
 *
 * Every name a campaign has had keeps answering, so a printed flyer survives a
 * rename. A name that reads as a short id is refused, because the id is tried
 * first and would shadow it.
 */
const ALIAS_SHAPE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/
export const CAMPAIGN_ALIAS_MIN = 3
export const CAMPAIGN_ALIAS_MAX = 60

/** Why a name cannot be a campaign's alias, or null when it can. */
export function campaignAliasProblem(alias: string): string | null {
  if (alias.length < CAMPAIGN_ALIAS_MIN || alias.length > CAMPAIGN_ALIAS_MAX) {
    return `Use ${CAMPAIGN_ALIAS_MIN} to ${CAMPAIGN_ALIAS_MAX} characters.`
  }
  if (!ALIAS_SHAPE.test(alias)) {
    return 'Use lowercase letters and digits, with hyphens between words.'
  }
  if (normalizeShortId(alias)) {
    return 'Eight letters and digits read as an id, even with hyphens. Add a word, or a few more letters.'
  }
  return null
}

/**
 * What a campaign address segment names: an id, or an alias. An id is read
 * first, the way an org's is; anything else is an alias when it has an alias's
 * shape, and null otherwise, so a malformed address is refused without a lookup.
 */
export function campaignAddress(
  segment: string,
): { shortId: string } | { alias: string } | null {
  const shortId = normalizeShortId(segment)
  if (shortId) return { shortId }
  const alias = segment.toLowerCase()
  return campaignAliasProblem(alias) === null ? { alias } : null
}
